import { useEffect, useRef, useState, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AdminPanel from "./pages/AdminPanel";
import Register from "./pages/Register";
import Discover from "./pages/Discover";
import Matches from "./pages/Matches";
import Chat from "./pages/Chat";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Credits from "./pages/Credits";
import WhoLikedMe from "./pages/WhoLikedMe";
import VerificationSimple from "./pages/VerificationSimple";
import Notifications from "./pages/Notifications";
import UserProfile from "./pages/UserProfile";
import Settings from "./pages/Settings";
import ProfilePreview from "./pages/ProfilePreview";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Favorites from "./pages/Favorites";
import BlockedUsers from "./pages/BlockedUsers";
import TermsOfService from "./pages/TermsOfService";
import PaywallHost from "./components/Paywall";
import RealtimeNotifier from "./components/RealtimeNotifier";
import { Toaster } from "./components/ui/sonner";

// Components
import { NotificationPrompt } from "./hooks/useNotifications";
import MotivationPopup from "./components/MotivationPopup";
import { useMotivationMessages } from "./hooks/useMotivationMessages";

// Initialize Firebase
import './firebase';

import { API } from "./config";
import { tx } from "./lib/tx";
import { MollieCheckoutHost } from "./components/checkout/MollieCheckout";

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Theme Context
const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

// Auth Callback Handler
const ChildSafetyRedirect = () => { window.location.replace("/child-safety.html"); return null; };

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      // Fonction avec retry pour gérer les erreurs réseau temporaires
      const authenticateWithRetry = async (retries = 3) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            console.log(`Auth attempt ${attempt}/${retries}...`);
            
            const response = await fetch(`${API}/auth/session`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ session_id: sessionId })
            });
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.detail || "Échec de la connexion");
            }
            
            const data = await response.json();
            
            if (data.user) {
              localStorage.setItem("user", JSON.stringify(data.user));
              localStorage.setItem("session_token", data.session_token);
              // Handle onboarding: reset for new users, mark complete for existing users
              if (data.is_new_user) {
                localStorage.removeItem("loveconnect_onboarding_completed");
              } else {
                localStorage.setItem("loveconnect_onboarding_completed", "true");
              }
              navigate("/discover", { state: { user: data.user }, replace: true });
              return; // Succès, on arrête
            } else {
              throw new Error("Données utilisateur manquantes");
            }
          } catch (err) {
            console.error(`Auth attempt ${attempt} failed:`, err.message);
            
            // Si c'est une erreur réseau et qu'on a encore des tentatives
            if (err.name === "TypeError" && err.message.includes("fetch") && attempt < retries) {
              console.log(`Retrying in ${attempt * 1000}ms...`);
              await new Promise(resolve => setTimeout(resolve, attempt * 1000));
              continue;
            }
            
            // Dernière tentative ou erreur non-réseau
            if (attempt === retries) {
              alert(err.message || tx("Erreur de connexion. Veuillez réessayer."));
              navigate("/login", { replace: true });
            }
          }
        }
      };
      
      authenticateWithRetry();
    }
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">{tx("Connexion en cours...")}</p>
      </div>
    </div>
  );
}

// Auto refresh session function
const refreshSession = async () => {
  try {
    const token = localStorage.getItem("session_token");
    if (!token) return null;
    
    const response = await fetch(`${API}/auth/refresh`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.session_token) {
        localStorage.setItem("session_token", data.session_token);
        return data;
      }
    }
    return null;
  } catch (error) {
    console.error("Session refresh failed:", error);
    return null;
  }
};

// Protected Route
function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle user data passed from AuthCallback (outside effect)
  const locationUser = location.state?.user;

  useEffect(() => {
    // Skip if user data passed from AuthCallback
    if (locationUser) {
      setUser(locationUser);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("session_token");
      
      if (storedUser && token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: {
              "Authorization": `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
            setIsAuthenticated(true);
          } else if (response.status === 401) {
            // Try to refresh the session
            const refreshed = await refreshSession();
            if (refreshed && refreshed.user) {
              setUser(refreshed.user);
              localStorage.setItem("user", JSON.stringify(refreshed.user));
              setIsAuthenticated(true);
            } else {
              localStorage.removeItem("user");
              localStorage.removeItem("session_token");
              setIsAuthenticated(false);
              navigate("/login");
            }
          } else {
            localStorage.removeItem("user");
            localStorage.removeItem("session_token");
            setIsAuthenticated(false);
            navigate("/login");
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          setIsAuthenticated(false);
          navigate("/login");
        }
      } else {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();

    // Auto refresh session every 30 minutes
    const refreshInterval = setInterval(async () => {
      const refreshed = await refreshSession();
      if (refreshed && refreshed.user) {
        setUser(refreshed.user);
        localStorage.setItem("user", JSON.stringify(refreshed.user));
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(refreshInterval);
  }, [navigate, locationUser]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
      <PaywallHost />
      <MollieCheckoutHost />
      <RealtimeNotifier />
    </AuthContext.Provider>
  );
}

// App Router
function AppRouter() {
  const location = useLocation();
  const authContext = useAuth();
  const user = authContext?.user || null;
  
  // Hook pour les messages de motivation
  const { showMotivation, motivationMessage, closeMotivation } = useMotivationMessages(user);
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
        <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
        <Route path="/chat/:userId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/profile/edit" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
        <Route path="/profile/preview" element={<ProtectedRoute><ProfilePreview /></ProtectedRoute>} />
        <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
        <Route path="/who-liked-me" element={<ProtectedRoute><WhoLikedMe /></ProtectedRoute>} />
        <Route path="/verification" element={<ProtectedRoute><VerificationSimple /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/user/:userId" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
        <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
        <Route path="/blocked-users" element={<ProtectedRoute><BlockedUsers /></ProtectedRoute>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/child-safety" element={<ChildSafetyRedirect />} />
      </Routes>
      {/* Notification Permission Prompt - only show when user is logged in */}
      {user && <NotificationPrompt />}
      
      {/* Popup de motivation quotidien */}
      {showMotivation && motivationMessage && (
        <MotivationPopup
          message={motivationMessage}
          onClose={closeMotivation}
          userName={user?.name}
        />
      )}
    </>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "dark";
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (
    <LanguageProvider>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <div className={`App min-h-screen ${theme === "light" ? "bg-gray-50" : "bg-background"}`}>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster theme={theme === "light" ? "light" : "dark"} position="top-center" richColors />
        </div>
      </ThemeContext.Provider>
    </LanguageProvider>
  );
}

export default App;
